import { TrendingUp, TrendingDown, Clock, CalendarCheck, AlertCircle, CheckCircle, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { cn } from '@/shared/lib/utils';
import { useTimesheet } from '../hooks/useTimeEntry';
import { useTimeEntry } from '../hooks/useTimeEntry';
import { useAdjustmentRequests } from '../hooks/useAdjustmentRequests';
import { useTimeAllocations } from '../hooks/useProjects';
import { formatHours, formatTimeBRT, getClockStatus, formatDateForAPI, toDateString, type Timesheet } from '../types';

// ==========================================
// Helpers de data
// ==========================================
function getMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: formatDateForAPI(start), endDate: formatDateForAPI(end) };
}

// ==========================================
// Card de métrica
// ==========================================
interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ElementType;
  variant?: 'default' | 'positive' | 'negative' | 'neutral';
  loading?: boolean;
}

function MetricCard({ title, value, subtitle, icon: Icon, variant = 'default', loading }: MetricCardProps) {
  const iconColors = {
    default: 'text-primary bg-primary/10',
    positive: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-950/40',
    negative: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-950/40',
    neutral: 'text-muted-foreground bg-muted',
  };
  const valueColors = {
    default: 'text-foreground',
    positive: 'text-green-600 dark:text-green-400',
    negative: 'text-red-600 dark:text-red-400',
    neutral: 'text-muted-foreground',
  };

  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        {loading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-7 w-16 bg-muted rounded" />
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{title}</p>
              <p className={cn('text-2xl font-bold font-mono', valueColors[variant])}>{value}</p>
              {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            </div>
            <div className={cn('p-2.5 rounded-xl', iconColors[variant])}>
              <Icon className="h-5 w-5" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// Mini-barra de progresso de horas
// ==========================================
function HoursProgressBar({
  worked,
  expected,
  label = 'Progresso do mês',
  colorClass,
}: {
  worked: number;
  expected: number;
  label?: string;
  colorClass?: string;
}) {
  const pct = expected > 0 ? Math.min((worked / expected) * 100, 100) : 0;
  const over = expected > 0 && worked >= expected - 0.01;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span>{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            colorClass
              ? colorClass
              : over
                ? 'bg-green-500'
                : pct > 75
                  ? 'bg-primary'
                  : pct > 40
                    ? 'bg-primary/70'
                    : 'bg-primary/40'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ==========================================
// DashboardPeoplePage
// ==========================================
export default function DashboardPeoplePage() {
  const { startDate, endDate } = getMonthRange();
  const { data: timesheet, isLoading: isLoadingSheet } = useTimesheet({ startDate, endDate });
  const { todayEntry } = useTimeEntry();
  const { data: pendingRequests } = useAdjustmentRequests({ status: 'PENDING' });
  const { data: allocations, isLoading: isLoadingAllocations } = useTimeAllocations({ startDate, endDate });

  // Totais do mês
  const totals = (timesheet ?? []).reduce(
    (acc, row: Timesheet) => ({
      worked: acc.worked + row.hoursWorked,
      expected: acc.expected + row.expectedHours,
      balance: acc.balance + row.hourBalance,
    }),
    { worked: 0, expected: 0, balance: 0 }
  );

  // Total de horas lançadas no mês
  const totalLaunched = (allocations ?? []).reduce((sum, a) => sum + a.hours, 0);

  // Dias com pelo menos 1 lançamento
  const daysWithLaunch = new Set(
    (allocations ?? []).map((a) => toDateString(a.date))
  ).size;

  // Dias trabalhados com horas não lançadas
  const workedDays = (timesheet ?? []).filter((r) => r.hoursWorked > 0).length;
  const daysWithoutLaunch = Math.max(0, workedDays - daysWithLaunch);

  // Saldo acumulado = último registro
  const lastEntry = timesheet && timesheet.length > 0
    ? timesheet[timesheet.length - 1]
    : null;
  const accumulatedBalance = lastEntry?.accumulatedBalance ?? 0;

  // Dias trabalhados no mês
  const totalDays = (timesheet ?? []).length;

  // Status de hoje
  const status = getClockStatus(todayEntry ?? null);
  const statusLabels: Record<string, { label: string; color: string }> = {
    idle: { label: 'Aguardando entrada', color: 'text-muted-foreground' },
    'clocked-in': { label: 'Trabalhando', color: 'text-green-600 dark:text-green-400' },
    'on-break': { label: 'Em intervalo', color: 'text-amber-600 dark:text-amber-400' },
    'clocked-out': { label: 'Expediente encerrado', color: 'text-blue-600 dark:text-blue-400' },
  };

  const pendingCount = pendingRequests?.length ?? 0;

  // Variante do card de lançamento
  const launchVariant = totalLaunched === 0
    ? 'neutral'
    : totalLaunched >= totals.worked - 0.01
      ? 'positive'
      : 'default';

  const monthLabel = new Date().toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Visão Geral</h1>
        <p className="text-muted-foreground mt-1 capitalize">{monthLabel}</p>
      </div>

      {/* Cards de métricas */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard
          title="Banco de Horas"
          value={`${accumulatedBalance >= 0 ? '+' : ''}${formatHours(accumulatedBalance)}`}
          subtitle="Saldo acumulado"
          icon={accumulatedBalance >= 0 ? TrendingUp : TrendingDown}
          variant={accumulatedBalance > 0 ? 'positive' : accumulatedBalance < 0 ? 'negative' : 'neutral'}
          loading={isLoadingSheet}
        />
        <MetricCard
          title="Saldo do Mês"
          value={`${totals.balance >= 0 ? '+' : ''}${formatHours(totals.balance)}`}
          subtitle={`${formatHours(totals.worked)} trabalhado`}
          icon={Clock}
          variant={totals.balance > 0 ? 'positive' : totals.balance < 0 ? 'negative' : 'neutral'}
          loading={isLoadingSheet}
        />
        <MetricCard
          title="Dias Trabalhados"
          value={`${workedDays}`}
          subtitle={totalDays > 0 ? `de ${totalDays} dias úteis` : undefined}
          icon={CalendarCheck}
          variant="default"
          loading={isLoadingSheet}
        />
        <MetricCard
          title="Horas Lançadas"
          value={formatHours(totalLaunched)}
          subtitle={
            totalLaunched === 0
              ? 'Nenhuma hora lançada'
              : daysWithoutLaunch > 0
                ? `${daysWithoutLaunch} dia${daysWithoutLaunch > 1 ? 's' : ''} sem lançamento`
                : 'Todos os dias lançados'
          }
          icon={Briefcase}
          variant={launchVariant}
          loading={isLoadingAllocations}
        />
        <MetricCard
          title="Ajustes Pendentes"
          value={`${pendingCount}`}
          subtitle={pendingCount === 0 ? 'Nenhum pendente' : pendingCount === 1 ? '1 aguardando revisão' : `${pendingCount} aguardando revisão`}
          icon={pendingCount === 0 ? CheckCircle : AlertCircle}
          variant={pendingCount === 0 ? 'positive' : 'negative'}
        />
      </div>

      {/* Progresso + status de hoje */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Progresso de horas do mês */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Horas do Mês</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingSheet || isLoadingAllocations ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-full" />
                <div className="h-2 bg-muted rounded w-full" />
                <div className="h-4 bg-muted rounded w-full" />
                <div className="h-2 bg-muted rounded w-full" />
              </div>
            ) : (
              <>
                {/* Barra de horas trabalhadas */}
                <HoursProgressBar worked={totals.worked} expected={totals.expected} label="Horas trabalhadas" />

                {/* Barra de horas lançadas */}
                <HoursProgressBar
                  worked={totalLaunched}
                  expected={totals.worked}
                  label="Horas lançadas em projetos"
                  colorClass={
                    totalLaunched >= totals.worked - 0.01
                      ? 'bg-green-500'
                      : totalLaunched > 0
                        ? 'bg-amber-500'
                        : 'bg-muted-foreground/30'
                  }
                />

                <div className="grid grid-cols-4 gap-3 text-center pt-1">
                  <div>
                    <p className="text-xs text-muted-foreground">Trabalhado</p>
                    <p className="font-mono font-semibold text-sm">{formatHours(totals.worked)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Esperado</p>
                    <p className="font-mono font-semibold text-sm text-muted-foreground">{formatHours(totals.expected)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Lançado</p>
                    <p className={cn(
                      'font-mono font-semibold text-sm',
                      totalLaunched >= totals.worked - 0.01
                        ? 'text-green-600 dark:text-green-400'
                        : totalLaunched > 0
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-muted-foreground'
                    )}>
                      {formatHours(totalLaunched)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Saldo</p>
                    <p className={cn(
                      'font-mono font-semibold text-sm',
                      totals.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    )}>
                      {totals.balance >= 0 ? '+' : ''}{formatHours(totals.balance)}
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Status de hoje */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hoje</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className={cn('text-sm font-medium', statusLabels[status].color)}>
                  {statusLabels[status].label}
                </span>
              </div>

              {todayEntry?.clockIn && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Entrada</span>
                  <span className="text-sm font-mono font-medium">{formatTimeBRT(todayEntry.clockIn)}</span>
                </div>
              )}
              {todayEntry?.breakStart && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Saída almoço</span>
                  <span className="text-sm font-mono font-medium">{formatTimeBRT(todayEntry.breakStart)}</span>
                </div>
              )}
              {todayEntry?.breakEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Volta almoço</span>
                  <span className="text-sm font-mono font-medium">{formatTimeBRT(todayEntry.breakEnd)}</span>
                </div>
              )}
              {todayEntry?.clockOut && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Saída</span>
                  <span className="text-sm font-mono font-medium">{formatTimeBRT(todayEntry.clockOut)}</span>
                </div>
              )}

              {!todayEntry && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  Nenhum registro hoje.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
